/**
 * Formats when something was saved: the clock time while it is still today, the calendar date once
 * it is not.
 *
 * This is the third timestamp formatter in this folder, and the three answer different questions.
 * `formatRelativeTimeLabel` answers "how long ago" and never names a date, which suits a list of
 * projects being scanned for freshness. This one answers "when", which is what a version list needs:
 * a reviewer comparing revisions wants the day they were written, not an elapsed span that slides
 * every time the panel is opened.
 *
 * `formatChatMessageTimestamp` is the closest neighbour and deliberately not reused — it drops the
 * year ("Jun 26"), which is fine beside a message in a conversation you are reading now and wrong on
 * a revision that may be a year old.
 *
 * Both functions return an **empty string** when there is no usable timestamp, so a caller can pick
 * its own fallback rather than printing a date the data never established.
 */

const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
const DATE_FORMAT: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };

/** "Today 01:05 PM" while it is still today, "05/02/2026" afterwards. */
export function formatSavedAtLabel(value: Date | string | null | undefined): string {
  const date = parseSavedAt(value);
  if (!date) {
    return '';
  }

  return isToday(date) ? `Today ${formatTimeOfDay(date)}` : formatDate(date);
}

/** The same, without the "Today" — for a sentence that already says it, such as "Last saved 01:05 PM". */
export function formatSavedAtStamp(value: Date | string | null | undefined): string {
  const date = parseSavedAt(value);
  if (!date) {
    return '';
  }

  return isToday(date) ? formatTimeOfDay(date) : formatDate(date);
}

function parseSavedAt(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isToday(date: Date): boolean {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
}

function formatTimeOfDay(date: Date): string {
  return date.toLocaleTimeString('en-US', TIME_FORMAT);
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', DATE_FORMAT);
}
